import React, { useState, useEffect, forwardRef } from 'react';
import {
  Modal,
  Button,
  Form,
  Col,
  Row,
  Table,
  Spinner,
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { useCurrency } from '../../context/currencyContext';

// ============================================================
// THEME & CONSTANTS
// ============================================================
const themeColor = '#004aad';
const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

/* ============================================================
   AUTH HELPERS
   Builds request headers with every plausible auth token key
   so whichever mechanism the backend uses will be satisfied.
   Also reads Authorization from multiple common localStorage keys.
============================================================ */
const buildAuthHeaders = (userLabId, extra = {}) => {
  const headers = {
    // Send ONLY ONE casing — browsers/caches/proxies can merge duplicate
    // headers by case-insensitive name and produce "00000004,00000004".
    userlabid: userLabId,
    'Content-Type': 'application/json',
    ...extra,
  };
  const tokenKeys = ['token', 'authToken', 'jwt', 'accessToken', 'idToken'];
  const token = tokenKeys.map((k) => localStorage.getItem(k)).find(Boolean);
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};
// Sends cookies ONLY when a token exists in localStorage.
// Sending cookies unconditionally can cause the backend to treat
// the request as a different (empty) lab session and return 200
// with an empty array — the classic "200 but no data" symptom.
const hasAuthToken = () =>
  ['token', 'authToken', 'jwt', 'accessToken', 'idToken'].some((k) =>
    Boolean(localStorage.getItem(k))
  );

const apiFetch = (url, options = {}) =>
  fetch(url, {
    credentials: hasAuthToken() ? 'include' : 'same-origin',
    ...options,
  });

// Clears stale auth data so the user is forced to re-login
const clearStaleAuth = () => {
  try {
    localStorage.removeItem('userLabId');
    ['token', 'authToken', 'jwt', 'accessToken', 'idToken'].forEach((k) =>
      localStorage.removeItem(k)
    );
    sessionStorage.clear();
  } catch (e) {
    /* ignore */
  }
};

// ============================================================
// INLINE STYLES (CreditMemo.css ka exact JS version)
// ============================================================
const styles = {
  modalHeader: {
    backgroundColor: '#0052cc',
    color: '#fff',
    borderBottom: 'none',
    height: '42px',
    padding: '0 1rem',
    borderRadius: '4px 4px 0 0',
  },
  modalBody: {
    padding: '20px 24px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  mainHeader: {
    fontSize: '1.4rem',
    fontWeight: 600,
    color: '#172b4d',
    marginBottom: '8px',
  },
  sectionTitle: {
    color: '#004aad',
    fontSize: '1.1rem',
    fontWeight: 500,
    lineHeight: 1.2,
    marginBottom: 0,
    paddingBottom: '4px',
    borderBottom: '1px solid #004aad',
  },
  optionalTitle: {
    color: '#004aad',
    fontSize: '1rem',
    fontWeight: 500,
    marginTop: '16px',
    marginBottom: '12px',
  },
  required: {
    color: 'red',
    marginLeft: '2px',
  },
  greyBox: {
    backgroundColor: '#f4f4f4',
    padding: '16px 16px 8px 16px',
    borderRadius: '0 0 3px 3px',
    marginTop: 0,
    marginBottom: '15px',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '12px',
  },
  fieldLabel: {
    width: '110px',
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#333',
    flexShrink: 0,
    marginBottom: 0,
  },
  fieldValue: {
    flex: 1,
    minWidth: 0,
  },
  dateContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  dateInput: {
    height: '36px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    borderRadius: '3px 0 0 3px',
    border: '1px solid #f4f4f4',
    borderRight: 'none',
    fontSize: '0.875rem',
    padding: '0 10px',
    width: '100%',
  },
  calendarBtn: {
    height: '36px',
    width: '38px',
    backgroundColor: '#004aad',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0 3px 3px 0',
    border: 'none',
    padding: 0,
  },
  formInput: {
    height: '36px',
    fontSize: '0.875rem',
    borderRadius: '3px',
    borderColor: '#dfe1e6',
  },
  tableContainer: {
    border: 'none',
    marginBottom: '15px',
    maxHeight: '320px',
    overflowY: 'auto',
  },
  invoiceTable: {
    marginBottom: 0,
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  tableHeader: {
    padding: '8px 10px',
    fontWeight: 600,
    fontSize: '0.875rem',
    backgroundColor: '#f4f5f7',
    color: '#172b4d',
    borderBottom: 'none',
  },
  tableCell: {
    padding: '10px 10px',
    verticalAlign: 'middle',
    fontSize: '0.875rem',
    color: '#172b4d',
    borderBottom: '1px solid #f4f4f4',
  },
  subText: {
    fontSize: '0.75rem',
    color: '#5e6c84',
    marginTop: '2px',
  },
  creditInput: {
    height: '32px',
    width: '90px',
    fontSize: '0.85rem',
    textAlign: 'left',
    borderRadius: '3px',
    borderColor: '#f4f4f4',
    backgroundColor: '#f4f4f4',
    display: 'inline-block',
  },
  memoLabel: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#172b4d',
    marginBottom: '6px',
  },
  textarea: {
    fontSize: '0.85rem',
    borderRadius: '3px',
    borderColor: '#f4f4f4',
    resize: 'none',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
    fontSize: '0.9rem',
  },
  summaryLabel: {
    color: '#172b4d',
    fontWeight: 600,
  },
  summaryValue: {
    color: '#172b4d',
    fontWeight: 500,
  },
  modalFooter: {
    borderTop: 'none',
    padding: '12px 24px',
  },
  saveButton: {
    backgroundColor: '#004aad',
    borderColor: '#004aad',
    color: '#f4f4f4',
    minWidth: '80px',
    fontWeight: 500,
    borderRadius: '3px',
  },
  filterBox: {
    backgroundColor: '#f4f4f4',
    padding: '12px 15px',
    border: '1px solid #f4f4f4',
    borderRadius: '3px',
    marginBottom: '12px',
  },
  filterLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    marginRight: '8px',
    whiteSpace: 'nowrap',
  },
};

// CSS injection: desktop rules (unchanged) + small screen responsive rules
const inlineCSSTag = `
.create-credit-modal .modal-content { border-radius: 4px !important; }
.create-credit-modal .modal-dialog { max-width: 800px !important; }
.create-credit-modal .react-datepicker-wrapper { width: 100%; }
.create-credit-modal .react-datepicker__input-container { width: 100%; }
.create-credit-modal .react-datepicker-popper { z-index: 9999 !important; }
.create-credit-modal input[type="checkbox"].form-check-input:checked {
  background-color: #0052cc !important;
  border-color: #0052cc !important;
}
.create-credit-modal .modal-header .btn-close {
  filter: invert(1) grayscale(100%) brightness(200%);
  opacity: 0.85;
}
/* TABLE: input ko cell ke andar properly align karein */
.create-credit-modal .credit-cell-wrapper {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
}
.create-credit-modal .credit-cell-wrapper .form-control {
  margin: 0;
}
.create-credit-modal .table-responsive {
  overflow-x: visible;
}

/* =====================================================
   SMALL SCREEN  -  layout desktop jaisa, sab kuch chhota
   --oc-fs = base font size, baaki sab em mein hai
===================================================== */
@media (max-width: 767px) {
  .create-credit-modal { --oc-fs: 7px; }

  .create-credit-modal .modal-dialog {
    max-width: 97% !important;
    width: 97% !important;
    margin: 4px auto !important;
  }

  .create-credit-modal .modal-content { font-size: var(--oc-fs) !important; }

  /* ---------- header ---------- */
  .create-credit-modal .modal-header {
    height: 3.2em !important;
    min-height: 0 !important;
    padding: 0 0.8em !important;
  }
  .create-credit-modal .modal-header .btn-close {
    width: 0.8em !important;
    height: 0.8em !important;
    padding: 0.4em !important;
    background-size: 0.8em !important;
  }

  /* ---------- body ---------- */
  .create-credit-modal .modal-body { padding: 8px 10px !important; }

  .create-credit-modal .oc-main-header {
    font-size: 1.7em !important;
    margin-bottom: 0.4em !important;
    line-height: 1.2 !important;
  }
  .create-credit-modal .oc-section {
    font-size: 1.4em !important;
    padding-bottom: 1px !important;
    line-height: 1.2 !important;
  }
  .create-credit-modal .oc-optional {
    font-size: 1.3em !important;
    margin-top: 0.8em !important;
    margin-bottom: 0.6em !important;
  }

  .create-credit-modal .alert {
    font-size: 1em !important;
    padding: 3px 6px !important;
    margin-top: 4px !important;
    margin-bottom: 4px !important;
  }

  .create-credit-modal .oc-grey {
    padding: 0.8em 1em 0.2em 1em !important;
    margin-bottom: 0.8em !important;
  }

  /* rows: 2 columns hamesha side by side */
  .create-credit-modal .row { --bs-gutter-x: 0.8em; }
  .create-credit-modal .row > .col-md-6 {
    flex: 0 0 auto !important;
    width: 50% !important;
    max-width: 50% !important;
  }
  .create-credit-modal .mt-3 { margin-top: 0.6em !important; }
  .create-credit-modal .py-3 { padding-top: 0.6em !important; padding-bottom: 0.6em !important; }
  .create-credit-modal .p-4 { padding: 1em !important; }

  /* ---------- label + field rows ---------- */
  .create-credit-modal .oc-row { margin-bottom: 0.7em !important; }
  .create-credit-modal .oc-label {
    width: 36% !important;
    flex: 0 0 36% !important;
    min-width: 0 !important;
    font-size: 1em !important;
    line-height: 1.15 !important;
    padding-right: 0.4em !important;
    margin-bottom: 0 !important;
  }
  .create-credit-modal .oc-field { min-width: 0 !important; flex: 1 1 0 !important; }

  /* ---------- inputs / select / date ---------- */
  .create-credit-modal .oc-input {
    height: 2.5em !important;
    min-height: 0 !important;
    font-size: 1em !important;
    line-height: 1.2 !important;
    padding: 0 0.5em !important;
    min-width: 0 !important;
    border-radius: 2px !important;
  }
  .create-credit-modal select.oc-input {
    padding-right: 1.8em !important;
    background-size: 0.8em !important;
    background-position: right 0.4em center !important;
  }

  .create-credit-modal .oc-date-input {
    height: 2.5em !important;
    min-height: 0 !important;
    font-size: 1em !important;
    padding: 0 0.5em !important;
  }
  .create-credit-modal .oc-cal-btn {
    height: 2.5em !important;
    width: 3em !important;
    flex-shrink: 0 !important;
  }
  .create-credit-modal .oc-cal-btn svg { width: 1.1em !important; height: 1.1em !important; }

  /* react-select */
  .create-credit-modal .oc-select__control {
    min-height: 2.5em !important;
    height: 2.5em !important;
    font-size: 1em !important;
  }
  .create-credit-modal .oc-select__value-container {
    height: 2.3em !important;
    padding: 0 0.5em !important;
  }
  .create-credit-modal .oc-select__indicators { height: 2.3em !important; }
  .create-credit-modal .oc-select__indicator { padding: 0 0.3em !important; }
  .create-credit-modal .oc-select__indicator svg { width: 1.2em !important; height: 1.2em !important; }
  .create-credit-modal .oc-select__indicator-separator { display: none !important; }
  .create-credit-modal .oc-select__single-value,
  .create-credit-modal .oc-select__placeholder,
  .create-credit-modal .oc-select__input-container,
  .create-credit-modal .oc-select__input {
    font-size: 1em !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  .create-credit-modal .oc-select__menu { font-size: var(--oc-fs) !important; }
  .create-credit-modal .oc-select__option {
    font-size: 1em !important;
    padding: 0.5em 0.7em !important;
  }

  /* datepicker popup */
  .create-credit-modal .react-datepicker { font-size: calc(var(--oc-fs) * 1.3) !important; }
  .create-credit-modal .react-datepicker__header { padding-top: 0.4em !important; }
  .create-credit-modal .react-datepicker__current-month,
  .create-credit-modal .react-datepicker__day-name,
  .create-credit-modal .react-datepicker__day {
    font-size: 1em !important;
    width: 1.7em !important;
    line-height: 1.7em !important;
    margin: 0.1em !important;
  }
  .create-credit-modal .react-datepicker__navigation {
    top: 0.3em !important;
    width: 2em !important;
    height: 2em !important;
  }
  .create-credit-modal .react-datepicker__navigation-icon::before {
    width: 0.6em !important;
    height: 0.6em !important;
    border-width: 1px 1px 0 0 !important;
  }

  /* ---------- table ---------- */
  .create-credit-modal .oc-table-container {
    margin-bottom: 0.8em !important;
    max-height: 22em !important;
    overflow-x: hidden !important;
  }
  .create-credit-modal .oc-table-container .table-responsive { overflow-x: visible !important; }

  .create-credit-modal .oc-table {
    width: 100% !important;
    table-layout: fixed !important;
    font-size: 1em !important;
  }
  .create-credit-modal .oc-table th,
  .create-credit-modal .oc-table td {
    font-size: 1em !important;
    padding: 0.4em 0.3em !important;
    line-height: 1.15 !important;
    white-space: normal !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
    vertical-align: middle !important;
  }
  .create-credit-modal .oc-table th:nth-child(1), .create-credit-modal .oc-table td:nth-child(1) { width: 7% !important; }
  .create-credit-modal .oc-table th:nth-child(2), .create-credit-modal .oc-table td:nth-child(2) { width: 27% !important; }
  .create-credit-modal .oc-table th:nth-child(3), .create-credit-modal .oc-table td:nth-child(3) { width: 18% !important; }
  .create-credit-modal .oc-table th:nth-child(4), .create-credit-modal .oc-table td:nth-child(4) { width: 16% !important; }
  .create-credit-modal .oc-table th:nth-child(5), .create-credit-modal .oc-table td:nth-child(5) { width: 16% !important; }
  .create-credit-modal .oc-table th:nth-child(6), .create-credit-modal .oc-table td:nth-child(6) { width: 16% !important; }

  .create-credit-modal .oc-sub-text {
    font-size: 0.9em !important;
    margin-top: 1px !important;
  }

  .create-credit-modal .oc-credit-input {
    width: 100% !important;
    height: 2.4em !important;
    min-height: 0 !important;
    font-size: 1em !important;
    padding: 0 0.3em !important;
  }

  /* checkboxes */
  .create-credit-modal input[type="checkbox"].form-check-input {
    width: 1.2em !important;
    height: 1.2em !important;
    margin: 0 !important;
    float: none !important;
  }
  .create-credit-modal .form-check {
    min-height: 0 !important;
    margin: 0 !important;
    padding-left: 0 !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }

  /* ---------- memo + summary ---------- */
  .create-credit-modal .oc-memo-label {
    font-size: 1em !important;
    margin-bottom: 0.3em !important;
  }
  .create-credit-modal .oc-memo {
    font-size: 1em !important;
    padding: 0.3em 0.5em !important;
    line-height: 1.2 !important;
  }
  .create-credit-modal .oc-summary-row {
    font-size: 1em !important;
    padding: 0.2em 0 !important;
  }
  .create-credit-modal .oc-summary-label,
  .create-credit-modal .oc-summary-value {
    font-size: 1em !important;
    line-height: 1.25 !important;
  }

  /* ---------- footer buttons ---------- */
  .create-credit-modal .modal-footer {
    padding: 0.6em 1em !important;
    flex-wrap: nowrap !important;
  }
  .create-credit-modal .oc-btn {
    font-size: 1em !important;
    padding: 0.4em 1.2em !important;
    line-height: 1.2 !important;
    min-width: 6em !important;
    border-radius: 2px !important;
  }
  .create-credit-modal .modal-footer > * { margin: 0 0 0 0.5em !important; }

  .create-credit-modal .spinner-border {
    width: 1.5em !important;
    height: 1.5em !important;
    border-width: 0.15em !important;
  }
}

@media (max-width: 480px) {
  .create-credit-modal { --oc-fs: 6px; }
}

@media (max-width: 360px) {
  .create-credit-modal { --oc-fs: 5px; }
}
`;

const OutsourceCredit = ({ isOpen, onClose }) => {
  // ============================================================
  // STATE — 100% SAME
  // ============================================================
  const [userLabId, setUserLabId] = useState('');
  const [backendError, setBackendError] = useState(false);
  // NEW: distinguish auth failure (401/403) from a real connectivity failure
  const [authError, setAuthError] = useState(false);

  const [creditDate, setCreditDate] = useState(new Date());
  const [amount, setAmount] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [sourceLabs, setSourceLabs] = useState([]);
  const [loadingLabs, setLoadingLabs] = useState(true);
  const [error, setError] = useState('');
  const [selectedLab, setSelectedLab] = useState('');
  const [selectedLabId, setSelectedLabId] = useState('');
  const [selectedLabName, setSelectedLabName] = useState('');
  const [memo, setMemo] = useState('');
  const [amountToApply, setAmountToApply] = useState(0);
  const [unappliedCredit, setUnappliedCredit] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [creditType, setCreditType] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [lastStatementOnly, setLastStatementOnly] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // ✅ Backend-driven currency — replaces every hardcoded '£' below
  const { formatAmount } = useCurrency();

  // ============================================================
  // BACKEND LOGIC — 100% SAME
  // ============================================================

  useEffect(() => {
    if (isOpen) {
      const storedUserLabId = localStorage.getItem('userLabId');
      if (storedUserLabId) {
        const cleanedId = String(storedUserLabId).trim();
        setUserLabId(cleanedId);
        setBackendError(false);
        setAuthError(false);
      } else {
        setError('User identification not found. Please log in again.');
        setAuthError(true);
      }
    }
  }, [isOpen]);

  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      border: '1px solid #dfe1e6',
      borderRadius: '3px',
      minHeight: '36px',
      height: '36px',
      boxShadow: 'none',
      fontSize: '0.875rem',
      '&:hover': { border: '1px solid #dfe1e6' },
    }),
    valueContainer: (p) => ({ ...p, height: '36px', padding: '0 8px' }),
    indicatorsContainer: (p) => ({ ...p, height: '36px' }),
    menu: (p) => ({
      ...p,
      zIndex: 9999,
      borderRadius: '3px',
      border: '1px solid #dfe1e6',
      boxShadow: '0 0.5rem 1rem rgba(0,0,0,.15)',
    }),
    option: (p, s) => ({
      ...p,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: '0.875rem',
      backgroundColor: s.isSelected ? themeColor : 'white',
      color: s.isSelected ? 'white' : 'black',
      '&:hover': {
        backgroundColor: s.isSelected ? themeColor : '#f4f5f7',
        color: s.isSelected ? 'white' : 'black',
      },
    }),
    singleValue: (p) => ({ ...p, color: '#172b4d' }),
    placeholder: (p) => ({ ...p, color: '#6c757d' }),
  };

  // Fetch source labs
  useEffect(() => {
    const fetchSourceLabs = async () => {
      try {
        setLoadingLabs(true);
        setError('');
        setAuthError(false);
        setBackendError(false);
        if (!userLabId) {
          setLoadingLabs(false);
          return;
        }
        console.log('Fetching source labs for user:', userLabId);
        const response = await apiFetch(
          `${API_BASE_URL}/api/outsource-invoice/source-labs`,
          {
            method: 'GET',
            headers: buildAuthHeaders(userLabId),
          }
        );
        console.log('Source labs response status:', response.status);

        // AUTH failure — clear stale auth so the user can re-login.
        if (response.status === 401 || response.status === 403) {
          setSourceLabs([]);
          setAuthError(true);
          setBackendError(false);
          clearStaleAuth();
          setError(
            response.status === 401
              ? 'Session expired (401). Your login is no longer valid — please log out and log in again.'
              : 'You do not have permission to view accounts (403).'
          );
          return;
        }

        if (!response.ok) {
          if (response.status === 404) {
            setSourceLabs([]);
            return;
          }
          throw new Error(`Failed to fetch labs: ${response.status}`);
        }

        // Parse body defensively — a 200 does NOT guarantee JSON
        const rawText = await response.text();
        console.log('Source labs RAW response body:', rawText);

        let parsed;
        try {
          parsed = rawText ? JSON.parse(rawText) : null;
        } catch (e) {
          console.error('Source labs: response was not valid JSON:', e);
          setSourceLabs([]);
          setError('Server returned an invalid response. Please try again.');
          return;
        }

        // Accept ANY of the common payload shapes the backend might use.
        // This eliminates the "200 OK but empty dropdown" class of bugs.
        const extractLabsArray = (payload) => {
          if (!payload) return null;
          if (Array.isArray(payload)) return payload;                 // [...]
          if (Array.isArray(payload.data)) return payload.data;       // { data: [...] }
          if (Array.isArray(payload.labs)) return payload.labs;       // { labs: [...] }
          if (Array.isArray(payload.result)) return payload.result;   // { result: [...] }
          if (Array.isArray(payload.rows)) return payload.rows;       // { rows: [...] }
          if (Array.isArray(payload.data?.labs)) return payload.data.labs; // { data: { labs: [...] } }
          return null;
        };

        const labs = extractLabsArray(parsed);

        // Only bail out when the backend explicitly signals failure.
        // A 200 with no recognizable array is treated as "no labs",
        // not as a connection/auth error, so the rest of the form
        // stays usable.
        if (parsed && parsed.success === false) {
          console.warn('Source labs: backend returned success=false', parsed);
          setSourceLabs([]);
          setBackendError(false);
          setAuthError(false);
          // Do NOT show a scary "server down" message for this —
          // it usually just means "this lab has no source labs".
          return;
        }

        if (labs === null) {
          console.warn('Source labs: 200 OK but no labs array found in payload:', parsed);
          setSourceLabs([]);
          setBackendError(false);
          setAuthError(false);
          return;
        }

        // Normalize to { labId, labName } so downstream code never breaks
        const normalized = labs
          .map((l) => {
            if (!l || typeof l !== 'object') return null;
            const labId =
              l.labId ?? l.lab_id ?? l.id ?? l.labID ?? l.value ?? l.LabId;
            const labName =
              l.labName ?? l.lab_name ?? l.name ?? l.labname ?? l.label ?? l.LabName;
            if (labId === undefined || labId === null) return null;
            return { ...l, labId: String(labId), labName: String(labName ?? labId) };
          })
          .filter(Boolean);

        console.log('Source labs: parsed', normalized.length, 'labs');
        setSourceLabs(normalized);
        setBackendError(false);
        setAuthError(false);
      } catch (fetchError) {
        console.error('Error fetching source labs:', fetchError);
        // Only a genuine network failure should flag backendError.
        const isNetworkError =
          fetchError instanceof TypeError ||
          /Failed to fetch|NetworkError|ECONNREFUSED/i.test(fetchError.message || '');
        if (isNetworkError) {
          setBackendError(true);
          setError('Failed to connect to server. Please ensure the backend server is running.');
        } else {
          setBackendError(false);
          setError(fetchError.message || 'Failed to load accounts.');
        }
        setSourceLabs([]);
      } finally {
        setLoadingLabs(false);
      }
    };
    if (isOpen && userLabId) fetchSourceLabs();
    else if (isOpen && !userLabId) {
      setLoadingLabs(false);
      setSourceLabs([]);
    }
  }, [isOpen, userLabId]);

  // Update selected lab
  useEffect(() => {
    if (selectedLab) {
      const lab = sourceLabs.find((l) => l.labId === selectedLab);
      setSelectedLabId(lab ? lab.labId : '');
      setSelectedLabName(lab ? lab.labName : '');
    } else {
      setSelectedLabId('');
      setSelectedLabName('');
      setSelectedInvoices([]);
      setReferenceNo('');
      setAmount('');
      setAmountToApply(0);
      setUnappliedCredit(0);
      setTableData([]);
    }
  }, [selectedLab, sourceLabs]);

  // Fetch invoices
  useEffect(() => {
    const fetchInvoices = async () => {
      if (!selectedLabId) {
        setTableData([]);
        setSelectedInvoices([]);
        setReferenceNo('');
        setAmount('');
        setAmountToApply(0);
        setUnappliedCredit(0);
        return;
      }
      try {
        setLoadingInvoices(true);
        setError('');
        const params = new URLSearchParams();
        params.append('sourceLabId', selectedLabId);
        if (startDate) params.append('startDate', format(startDate, 'yyyy-MM-dd'));
        if (endDate) params.append('endDate', format(endDate, 'yyyy-MM-dd'));
        params.append('invoiceStatus', 'unpaid,partially_paid,overdue,pending');

        const response = await apiFetch(
          `${API_BASE_URL}/api/outsource-invoice/for-payment?${params.toString()}`,
          {
            method: 'GET',
            headers: buildAuthHeaders(userLabId),
          }
        );

        if (response.status === 401 || response.status === 403) {
          setAuthError(true);
          clearStaleAuth();
          setError('Session expired or not authorized. Please log in again.');
          setTableData([]);
          setSelectedInvoices([]);
          return;
        }

        if (!response.ok) throw new Error(`Failed to fetch invoices: ${response.status}`);

        const rawText = await response.text();
        let parsed;
        try {
          parsed = rawText ? JSON.parse(rawText) : null;
        } catch (e) {
          console.error('Invoices: response was not valid JSON:', e);
          setTableData([]);
          setSelectedInvoices([]);
          return;
        }
        console.log('Invoices RAW response body:', parsed);

        const extractInvoicesArray = (payload) => {
          if (!payload) return null;
          if (Array.isArray(payload)) return payload;
          if (Array.isArray(payload.data)) return payload.data;
          if (Array.isArray(payload.invoices)) return payload.invoices;
          if (Array.isArray(payload.result)) return payload.result;
          if (Array.isArray(payload.rows)) return payload.rows;
          if (Array.isArray(payload.data?.invoices)) return payload.data.invoices;
          return null;
        };

        const invoices = extractInvoicesArray(parsed);
        if (invoices === null) {
          console.warn('Invoices: 200 OK but no invoices array found in payload:', parsed);
          setTableData([]);
          setSelectedInvoices([]);
          return;
        }

        const formattedInvoices = invoices.map((invoice) => ({
          ...invoice,
          originalRemaining: invoice.remaining,
          payment: 0,
        }));
        setTableData(formattedInvoices);
        const updatedSelectedInvoices = selectedInvoices.filter((id) =>
          formattedInvoices.some((row) => row.id === id)
        );
        setSelectedInvoices(updatedSelectedInvoices);
      } catch (invoiceError) {
        console.error('Error fetching invoices:', invoiceError);
        setError('Error fetching invoices: ' + invoiceError.message);
        setTableData([]);
        setSelectedInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    };
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLabId, startDate, endDate, userLabId]);

  // Reference No.
  useEffect(() => {
    const selectedRows = tableData.filter((row) => selectedInvoices.includes(row.id));
    const invoiceNumbers = selectedRows.map((row) => row.invoiceNumber);
    setReferenceNo(invoiceNumbers.join(', '));
  }, [selectedInvoices, tableData]);

  // Applied / Unapplied
  useEffect(() => {
    const totalApplied = tableData.reduce((sum, row) => sum + parseFloat(row.payment || 0), 0);
    setAmountToApply(totalApplied);
    const numericAmount = parseFloat(amount || 0);
    if (!isNaN(numericAmount)) {
      const unapplied = numericAmount - totalApplied;
      setUnappliedCredit(unapplied > 0 ? unapplied : 0);
    } else setUnappliedCredit(0);
  }, [amount, tableData]);

  // Auto amount from selected invoices
  useEffect(() => {
    const calculateTotalFromSelectedInvoices = () => {
      return selectedInvoices.reduce((sum, invoiceId) => {
        const invoice = tableData.find((row) => row.id === invoiceId);
        return sum + (invoice ? parseFloat(invoice.payment || 0) : 0);
      }, 0);
    };
    if (selectedInvoices.length > 0) {
      const totalPayment = calculateTotalFromSelectedInvoices();
      setAmount(totalPayment > 0 ? totalPayment.toFixed(2) : '');
    }
  }, [selectedInvoices, tableData]);

  // ============================================================
  // HANDLERS — 100% SAME
  // ============================================================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'lab') setSelectedLab(value);
    else if (name === 'amount') {
      setAmount(value);
      if (value && !isNaN(parseFloat(value)) && selectedLabId) {
        const enteredAmount = parseFloat(value);
        let remainingAmount = enteredAmount;
        const newSelectedInvoices = [];
        const updatedTableData = tableData.map((row) => ({
          ...row,
          payment: 0,
          remaining: row.originalRemaining,
        }));
        const sortedInvoices = [...updatedTableData].sort((a, b) => {
          const dateA = new Date(a.invoiceDate);
          const dateB = new Date(b.invoiceDate);
          if (dateA < dateB) return -1;
          if (dateA > dateB) return 1;
          return a.invoiceNumber.localeCompare(b.invoiceNumber);
        });
        const finalTableData = sortedInvoices.map((row) => {
          if (remainingAmount <= 0) return row;
          if (row.originalRemaining > 0) {
            if (remainingAmount >= row.originalRemaining) {
              newSelectedInvoices.push(row.id);
              remainingAmount -= row.originalRemaining;
              return { ...row, payment: row.originalRemaining, remaining: 0 };
            } else {
              newSelectedInvoices.push(row.id);
              const paymentAmount = remainingAmount;
              remainingAmount = 0;
              return {
                ...row,
                payment: paymentAmount,
                remaining: row.originalRemaining - paymentAmount,
              };
            }
          }
          return row;
        });
        setSelectedInvoices(newSelectedInvoices);
        setTableData(finalTableData);
      } else if (!value) {
        setSelectedInvoices([]);
        setTableData(
          tableData.map((row) => ({
            ...row,
            payment: 0,
            remaining: row.originalRemaining,
          }))
        );
      }
    } else if (name === 'memo') setMemo(value);
    else if (name === 'creditType') setCreditType(value);
  };

  const handleInvoiceSelection = (invoiceId) => {
    const isSelected = selectedInvoices.includes(invoiceId);
    if (isSelected) {
      setSelectedInvoices((prev) => prev.filter((id) => id !== invoiceId));
      setTableData((prev) =>
        prev.map((row) =>
          row.id === invoiceId ? { ...row, payment: 0, remaining: row.originalRemaining } : row
        )
      );
    } else {
      setSelectedInvoices((prev) => [...prev, invoiceId]);
      setTableData((prev) =>
        prev.map((row) =>
          row.id === invoiceId ? { ...row, payment: row.originalRemaining, remaining: 0 } : row
        )
      );
    }
  };

  const handleInvoicePaymentChange = (index, paymentValue) => {
    const updatedTableData = [...tableData];
    const row = updatedTableData[index];
    const newPayment = parseFloat(paymentValue || 0);
    if (row) {
      const maxAllowedPayment = row.originalRemaining;
      const appliedPayment = Math.min(newPayment, maxAllowedPayment);
      updatedTableData[index] = {
        ...row,
        payment: appliedPayment,
        remaining: row.originalRemaining - appliedPayment,
      };
      setTableData(updatedTableData);
    }
  };

  const handleSelectAll = () => {
    if (tableData.length === 0) return;
    const allInvoiceIds = tableData.map((row) => row.id);
    setSelectedInvoices(allInvoiceIds);
    setTableData(
      tableData.map((row) => ({ ...row, payment: row.originalRemaining, remaining: 0 }))
    );
  };

  const handleClearAll = () => {
    setSelectedInvoices([]);
    setTableData(
      tableData.map((row) => ({ ...row, payment: 0, remaining: row.originalRemaining }))
    );
    setAmount('');
  };

  const handleSave = async () => {
    if (!userLabId) {
      Swal.fire({
        title: 'Error!',
        text: 'Unable to process: User identification not found. Please log in again.',
        icon: 'error',
        confirmButtonColor: themeColor,
      });
      return;
    }
    if (!selectedLab || !creditDate || !creditType || !amount || !selectedInvoices.length) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields (*) and select at least one invoice.',
        icon: 'warning',
        confirmButtonColor: themeColor,
      });
      return;
    }
    const numericAmount = parseFloat(amount || 0);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      Swal.fire({
        title: 'Invalid Amount',
        text: 'Please enter a valid positive amount.',
        icon: 'warning',
        confirmButtonColor: themeColor,
      });
      return;
    }
    const appliedInvoices = tableData
      .filter((row) => selectedInvoices.includes(row.id) && parseFloat(row.payment || 0) > 0)
      .map((row) => {
        const existingPaid = parseFloat(row.paid || 0);
        const newPayment = parseFloat(row.payment || 0);
        const newPaidAmount = existingPaid + newPayment;
        return {
          invoiceId: row.id,
          invoiceNumber: row.invoiceNumber,
          caseNumber: row.caseNumber,
          appliedAmount: newPayment,
          newPaidAmount: newPaidAmount,
          remainingAfterCredit: row.remaining,
        };
      });
    if (appliedInvoices.length === 0) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please enter credit amounts for selected invoices.',
        icon: 'warning',
        confirmButtonColor: themeColor,
      });
      return;
    }
    const creditData = {
      sourceLabId: selectedLabId,
      sourceLabName: selectedLabName,
      amountReceived: numericAmount,
      creditDate: format(creditDate, 'yyyy-MM-dd'),
      creditType,
      referenceNo: referenceNo.split(', ').filter(Boolean),
      memo,
      appliedInvoices,
      finalAppliedAmount: amountToApply,
      finalUnappliedAmount: unappliedCredit,
      addedBy: userLabId,
      uniqueCreditId: `OSCR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/api/outsource-invoice/save-credit`,
        {
          method: 'POST',
          headers: buildAuthHeaders(userLabId),
          body: JSON.stringify(creditData),
        }
      );
      if (response.status === 401 || response.status === 403) {
        clearStaleAuth();
        throw new Error(
          'Session expired or not authorized. Please log out and log in again before saving.'
        );
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to save credit: ${response.status}`);
      }
      const responseData = await response.json();
      if (responseData.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Credit memo saved successfully!',
          icon: 'success',
          confirmButtonColor: themeColor,
        });
        handleCloseModal();
      } else throw new Error(responseData.message || 'Failed to save credit');
    } catch (error) {
      console.error('Error saving credit:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to save credit: ' + error.message,
        icon: 'error',
        confirmButtonColor: themeColor,
      });
    }
  };

  const handleCloseModal = () => {
    setSelectedLab('');
    setSelectedLabId('');
    setSelectedLabName('');
    setCreditDate(new Date());
    setAmount('');
    setReferenceNo('');
    setMemo('');
    setTableData([]);
    setSelectedInvoices([]);
    setAmountToApply(0);
    setUnappliedCredit(0);
    setError('');
    setLoadingLabs(true);
    setCreditType('');
    setStartDate(null);
    setEndDate(null);
    setLastStatementOnly(false);
    setBackendError(false);
    setAuthError(false);
    setLoadingInvoices(false);
    onClose();
  };

  // Custom date input — CreditMemo jaisa
  const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <div style={styles.dateContainer} onClick={onClick}>
      <input
        type="text"
        value={value || ''}
        ref={ref}
        readOnly
        className="form-control oc-date-input"
        style={styles.dateInput}
      />
      <button type="button" className="oc-cal-btn" style={styles.calendarBtn}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
          <line x1="7" y1="14" x2="7" y2="14"></line>
          <line x1="12" y1="14" x2="12" y2="14"></line>
          <line x1="17" y1="14" x2="17" y2="14"></line>
          <line x1="7" y1="18" x2="7" y2="18"></line>
          <line x1="12" y1="18" x2="12" y2="18"></line>
          <line x1="17" y1="18" x2="17" y2="18"></line>
        </svg>
      </button>
    </div>
  ));

  const allSelected = tableData.length > 0 && selectedInvoices.length === tableData.length;

  if (!isOpen) return null;

  return (
    <>
      <style>{inlineCSSTag}</style>

      <Modal
        show={isOpen}
        onHide={handleCloseModal}
        size="lg"
        centered
        className="create-credit-modal"
      >
        {/* Empty blue header like CreditMemo */}
        <Modal.Header closeButton style={styles.modalHeader} />

        <Modal.Body style={styles.modalBody}>
          <div className="oc-main-header" style={styles.mainHeader}>Create New Credit</div>

          <h5 className="oc-section" style={styles.sectionTitle}>Credit Details</h5>

          {error && <div className="alert alert-danger mt-2 py-2">{error}</div>}
          {authError && (
            <div className="alert alert-warning mt-2 py-2 d-flex align-items-center justify-content-between">
              <span>
                <strong>Authorization Issue:</strong> Your session has expired. Please log in again.
              </span>
              <Button
                size="sm"
                style={{ backgroundColor: themeColor, borderColor: themeColor }}
                onClick={() => {
                  clearStaleAuth();
                  window.location.href = '/login';
                }}
              >
                Log In Again
              </Button>
            </div>
          )}
          {backendError && (
            <div className="alert alert-warning mt-2 py-2">
              <strong>Connection Issue:</strong> Unable to connect to the server.
            </div>
          )}
          {!userLabId && isOpen && (
            <div className="alert alert-warning mt-2 py-2">
              <strong>Warning:</strong> User not identified. Please log in again.
            </div>
          )}

          {/* ============ GREY BOX ============ */}
          <div className="oc-grey" style={styles.greyBox}>
            <Row>
              <Col md={6}>
                {/* Account */}
                <Form.Group className="oc-row" style={styles.fieldRow}>
                  <Form.Label className="oc-label" style={styles.fieldLabel}>
                    Account<span style={styles.required}>*</span>
                  </Form.Label>
                  <div className="oc-field" style={styles.fieldValue}>
                    <Select
                      classNamePrefix="oc-select"
                      options={sourceLabs.map((lab) => ({
                        value: lab.labId,
                        label: lab.labName,
                      }))}
                      value={
                        selectedLab ? { value: selectedLab, label: selectedLabName } : null
                      }
                      onChange={(opt) =>
                        handleInputChange({
                          target: { name: 'lab', value: opt ? opt.value : '' },
                        })
                      }
                      placeholder={
                        loadingLabs
                          ? 'Loading accounts...'
                          : !userLabId
                          ? 'Please log in'
                          : sourceLabs.length === 0
                          ? 'No accounts found'
                          : 'Select Account...'
                      }
                      isDisabled={loadingLabs || !userLabId || backendError}
                      isClearable
                      styles={customStyles}
                    />
                  </div>
                </Form.Group>

                {/* Credit Date */}
                <Form.Group className="oc-row" style={styles.fieldRow}>
                  <Form.Label className="oc-label" style={styles.fieldLabel}>
                    Credit Date<span style={styles.required}>*</span>
                  </Form.Label>
                  <div className="oc-field" style={styles.fieldValue}>
                    <DatePicker
                      selected={creditDate}
                      onChange={(date) => setCreditDate(date)}
                      dateFormat="d MMM yyyy"
                      customInput={<CustomDateInput />}
                      disabled={!userLabId || backendError}
                    />
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                {/* Reference No. */}
                <Form.Group className="oc-row" style={styles.fieldRow}>
                  <Form.Label className="oc-label" style={styles.fieldLabel}>
                    Reference No.
                  </Form.Label>
                  <div className="oc-field" style={styles.fieldValue}>
                    <Form.Control
                      type="text"
                      placeholder="Optional - auto-gene"
                      name="referenceNo"
                      value={referenceNo}
                      readOnly
                      className="oc-input"
                      style={styles.formInput}
                    />
                  </div>
                </Form.Group>

                {/* Amount */}
                <Form.Group className="oc-row" style={styles.fieldRow}>
                  <Form.Label className="oc-label" style={styles.fieldLabel}>
                    Amount<span style={styles.required}>*</span>
                  </Form.Label>
                  <div className="oc-field" style={styles.fieldValue}>
                    <Form.Control
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      name="amount"
                      value={amount}
                      onChange={handleInputChange}
                      min="0"
                      className="oc-input"
                      style={styles.formInput}
                      disabled={!userLabId || backendError}
                    />
                  </div>
                </Form.Group>

                {/* Type */}
                <Form.Group className="oc-row" style={styles.fieldRow}>
                  <Form.Label className="oc-label" style={styles.fieldLabel}>
                    Type<span style={styles.required}>*</span>
                  </Form.Label>
                  <div className="oc-field" style={styles.fieldValue}>
                    <Form.Select
                      name="creditType"
                      value={creditType}
                      onChange={handleInputChange}
                      className="oc-input"
                      style={styles.formInput}
                      disabled={!userLabId || backendError}
                    >
                      <option value="">Select Credit Type</option>
                      <option value="credit_note">Credit Note</option>
                      <option value="refund">Refund</option>
                      <option value="adjustment">Adjustment</option>
                      <option value="write_off">Write Off</option>
                    </Form.Select>
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* ============ APPLY TO INVOICES HEADER (FILTERS REMOVED) ============ */}
          <div className="oc-optional" style={styles.optionalTitle}>
            Apply to Outstanding Invoices
          </div>

          {/* ============ INVOICE TABLE ============ */}
          {selectedLabId && userLabId && !backendError && (
            <>
              <div className="oc-table-container" style={styles.tableContainer}>
                {loadingInvoices ? (
                  <div className="text-center p-4">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  <Table responsive hover className="oc-table" style={styles.invoiceTable}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.tableHeader, width: '40px' }}>
                          <Form.Check
                            type="checkbox"
                            checked={allSelected}
                            onChange={allSelected ? handleClearAll : handleSelectAll}
                            disabled={!userLabId || backendError}
                          />
                        </th>
                        <th style={styles.tableHeader}>Description</th>
                        <th style={styles.tableHeader}>Invoice Date</th>
                        <th style={{ ...styles.tableHeader, textAlign: 'end' }}>Total</th>
                        <th style={{ ...styles.tableHeader, textAlign: 'end' }}>Remaining</th>
                        <th style={{ ...styles.tableHeader, textAlign: 'end', width: '120px' }}>
                          Credit
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.length === 0 ? (
                        <tr>
                          <td
                            colSpan="6"
                            className="text-center py-3"
                            style={styles.tableCell}
                          >
                            No outstanding invoices found for this account.
                          </td>
                        </tr>
                      ) : (
                        tableData.map((row, idx) => (
                          <tr key={row.id || idx}>
                            <td style={styles.tableCell}>
                              <Form.Check
                                type="checkbox"
                                checked={selectedInvoices.includes(row.id)}
                                onChange={() => handleInvoiceSelection(row.id)}
                                disabled={
                                  !userLabId || backendError || row.invoiceStatus === 'paid'
                                }
                              />
                            </td>
                            <td style={styles.tableCell}>
                              <div>{row.description}</div>
                              {row.caseNumber && (
                                <div className="oc-sub-text" style={styles.subText}>
                                  {row.caseNumber}
                                </div>
                              )}
                            </td>
                            <td style={styles.tableCell}>
                              {format(new Date(row.invoiceDate), 'd MMM yyyy')}
                            </td>
                            <td style={{ ...styles.tableCell, textAlign: 'end' }}>
                              {formatAmount(Number(row.total || 0))}
                            </td>
                            <td style={{ ...styles.tableCell, textAlign: 'end' }}>
                              {formatAmount(Number(row.remaining || 0))}
                            </td>
                            <td style={styles.tableCell}>
                              <div className="credit-cell-wrapper">
                                <Form.Control
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={row.payment === 0 ? '' : row.payment}
                                  onChange={(e) =>
                                    handleInvoicePaymentChange(idx, e.target.value)
                                  }
                                  disabled={
                                    !selectedInvoices.includes(row.id) ||
                                    !userLabId ||
                                    backendError ||
                                    row.invoiceStatus === 'paid'
                                  }
                                  className="oc-credit-input"
                                  style={styles.creditInput}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </div>

              {/* ============ MEMO + SUMMARY ============ */}
              <Row className="mt-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="oc-memo-label" style={styles.memoLabel}>
                      Memo
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Add any additional notes about this credit memo..."
                      name="memo"
                      value={memo}
                      onChange={handleInputChange}
                      className="oc-memo"
                      style={styles.textarea}
                      disabled={!userLabId || backendError}
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="d-flex flex-column justify-content-center">
                  <div className="oc-summary-row" style={styles.summaryRow}>
                    <span className="oc-summary-label" style={styles.summaryLabel}>
                      Total Amount
                    </span>
                    <span className="oc-summary-value" style={styles.summaryValue}>
                      {formatAmount(parseFloat(amount || 0))}
                    </span>
                  </div>
                  <div className="oc-summary-row" style={styles.summaryRow}>
                    <span className="oc-summary-label" style={styles.summaryLabel}>
                      Applied
                    </span>
                    <span className="oc-summary-value" style={styles.summaryValue}>
                      {formatAmount(amountToApply)}
                    </span>
                  </div>
                  <div className="oc-summary-row" style={styles.summaryRow}>
                    <span className="oc-summary-label" style={styles.summaryLabel}>
                      Unapplied
                    </span>
                    <span className="oc-summary-value" style={styles.summaryValue}>
                      {formatAmount(unappliedCredit)}
                    </span>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>

        <Modal.Footer style={styles.modalFooter}>
          <Button
            variant="light"
            className="oc-btn"
            onClick={handleCloseModal}
            style={{ minWidth: '80px' }}
          >
            Cancel
          </Button>
          <Button
            className="oc-btn"
            onClick={handleSave}
            style={{
              ...styles.saveButton,
              ...(!userLabId || backendError || loadingInvoices
                ? { opacity: 0.6, cursor: 'not-allowed' }
                : {}),
            }}
            disabled={!userLabId || backendError || loadingInvoices}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OutsourceCredit;